import { useForm, router } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import AdminLayout from '@/layouts/AdminLayout'

interface UserData {
  id: string
  name: string | null
  email_address: string
  role: 'admin' | 'operator'
  status: 'active' | 'deactivated'
  super_admin: boolean
  organization_id: string | null
  organization_name: string | null
  created_at: string
}

interface Organization {
  id: string
  name: string
  slug: string
}

interface Props {
  user: UserData
  organizations: Organization[]
}

export default function UsersEdit({ user, organizations }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    name: user.name || '',
    email_address: user.email_address,
    role: user.role,
    organization_id: user.organization_id || '',
    password: '',
    password_confirmation: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/admin/users/${user.id}`)
  }

  return (
    <>
      {/* Header */}
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <button
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent cursor-pointer"
            onClick={() => router.visit('/admin/users')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-sm font-semibold">{user.name || user.email_address}</h1>
          {user.status === 'active' ? (
            <Badge
              variant="secondary"
              size="sm"
              className="bg-emerald-50 text-emerald-600"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              size="sm"
              className="bg-red-100 text-red-800"
            >
              Deactivated
            </Badge>
          )}
          {user.super_admin && (
            <Badge
              variant="secondary"
              size="sm"
              className="bg-amber-50 text-amber-800"
            >
              Super Admin
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user.status === 'active' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => {
                if (confirm(`Deactivate ${user.name || user.email_address}? They will no longer be able to log in.`)) {
                  router.post(`/admin/users/${user.id}/deactivate`)
                }
              }}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              onClick={() => {
                router.post(`/admin/users/${user.id}/reactivate`)
              }}
            >
              Reactivate
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => router.visit('/admin/users')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="user-form"
            size="sm"
            className="text-xs"
            disabled={processing}
          >
            Save Changes
          </Button>
        </div>
      </header>

      <div className="max-w-xl p-8">
        <form id="user-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Organization */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Organization
            </h2>
            <div>
              <Label htmlFor="organization_id" className="text-xs">Organization</Label>
              <select
                id="organization_id"
                value={data.organization_id}
                onChange={(e) => setData('organization_id', e.target.value)}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
              >
                <option value="">No organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.slug})
                  </option>
                ))}
              </select>
              {errors.organization_id && <p className="mt-1 text-xs text-red-500">{errors.organization_id}</p>}
            </div>
          </section>

          {/* Account */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Account
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs">Name</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="email_address" className="text-xs">Email</Label>
                <Input
                  id="email_address"
                  type="email"
                  value={data.email_address}
                  onChange={(e) => setData('email_address', e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                  required
                />
                {errors.email_address && <p className="mt-1 text-xs text-red-500">{errors.email_address}</p>}
              </div>
              <div>
                <Label htmlFor="role" className="text-xs">Role</Label>
                <select
                  id="role"
                  value={data.role}
                  onChange={(e) => setData('role', e.target.value as 'admin' | 'operator')}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
                >
                  <option value="operator">Operator</option>
                  <option value="admin">Admin</option>
                </select>
                {errors.role && <p className="mt-1 text-xs text-red-500">{errors.role}</p>}
              </div>
            </div>
          </section>

          {/* Password */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Change Password
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-xs">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={(e) => setData('password', e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="mt-1 h-9 text-[13px]"
                />
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
              </div>
              <div>
                <Label htmlFor="password_confirmation" className="text-xs">Confirm Password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={data.password_confirmation}
                  onChange={(e) => setData('password_confirmation', e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="mt-1 h-9 text-[13px]"
                />
                {errors.password_confirmation && <p className="mt-1 text-xs text-red-500">{errors.password_confirmation}</p>}
              </div>
            </div>
          </section>
        </form>
      </div>
    </>
  )
}

UsersEdit.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
